import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
// @mui
import {
  Stack,
  IconButton,
  InputAdornment,
  TextField,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Typography,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
// components
import Iconify from '../../../components/iconify';
import Services from 'src/services/Services';

// ----------------------------------------------------------------------

export default function LoginForm() {
  const navigate = useNavigate();
  const location = useLocation();

  // View State Management: 'login' | 'first_login_update'
  const [currentView, setCurrentView] = useState('login');

  // Form States
  const [loginEmail, setLoginEmail] = useState("");
  const [loginPassword, setLoginPassword] = useState("");
  
  // Update Password States
  const [currentPasswordVal, setCurrentPasswordVal] = useState("");
  const [newPasswordVal, setNewPasswordVal] = useState("");
  const [confirmPasswordVal, setConfirmPasswordVal] = useState("");

  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [modal, setModal] = useState(false);
  const [html, setHtml] = useState('');
  const [isChecked, setChecked] = useState(false);
  const [tempUserData, setTempUserData] = useState(null);

  // --- LOGIN FUNCTION ---
  const handleLoginSubmit = (event) => {
    event.preventDefault();

    if (!loginEmail || !loginPassword) {
      toast.warning('Lütfen kullanıcı adı ve şifreyi giriniz.');
      return;
    }

    setLoading(true);
    Services.Login(loginEmail, loginPassword)
      .then((response) => {
        if (response.data.status) {
          const result = response.data.result;
          if (result?.isFirstLogin) {
            setTempUserData(result);
            setLoading(false);
            setCurrentView('first_login_update');
            return;
          }

          // Normal Giriş Başarılı
          handleAuthSuccess(result);
        } else {
          if (response.data.message === 'Password not match') {
            toast.error('Şifrenizi kontrol ediniz');
          } else {
            toast.error('Giriş bilgilerinizi kontrol ediniz');
          }
        }
      })
      .catch((error) => {
        toast.error(error.message);
      })
      .finally(() => {
        if (currentView === 'login') setLoading(false);
      });
  };

  const handleAuthSuccess = (result) => {
    localStorage.setItem('expired', JSON.stringify(result.expired));
    localStorage.setItem('user', JSON.stringify(result));
    localStorage.setItem('mallID', result?.mallID);
    localStorage.setItem('tokenForService', result?.token);
    
    const redirectPath = location.state?.from?.pathname || '/dashboard';
    navigate(redirectPath);
  };

  // --- UPDATE PASSWORD FUNCTION ---
  const handleUpdatePassword = (e) => {
    e.preventDefault();
    
    // Validasyonlar
    if (newPasswordVal.length < 4) {
      toast.warning("Şifreniz en az dört karakterden oluşmalıdır.");
      return;
    }
    if (newPasswordVal !== confirmPasswordVal) {
      toast.error('Yeni şifreler eşleşmemektedir.');
      return;
    }

    const token = tempUserData?.token || JSON.parse(localStorage.getItem('user'))?.token;
    if (!token) {
      toast.error("Oturum bilgisi bulunamadı. Lütfen yeniden giriş yapın.");
      setCurrentView('login');
      return;
    }

    setLoading(true);
    Services.EditPassword(newPasswordVal, token)
      .then((res) => {
        if (res.data.status) {
          toast.success("Şifreniz başarıyla güncellenmiştir.");

          if (tempUserData) handleAuthSuccess(tempUserData);
          else navigate('/dashboard');
        } else {
          toast.error("Şifre güncellenemedi: " + (res.data.message || ""));
          setLoading(false);
        }
      })
      .catch((err) => {
        toast.error("Bağlantı hatası: " + err.message);
        setLoading(false);
      });
  };

  const checkboxChange = () => {
    if (isChecked) {
      setChecked(false);
    } else {
      setModal(true);
    }
  };

  useEffect(() => {
     Services.GetAgreement('23986740293876492837')
    .then((response) => {
        setHtml(response.data.result.description);
    })
    .catch((error) => console.log(error));
  }, []);

  // --- RENDER VIEWS ---

  if (currentView === 'first_login_update') {
    return (
        <Stack spacing={3} sx={{ mt: 2 }}>

            <form onSubmit={handleUpdatePassword}>
                <Stack spacing={3}>                    
                    <TextField
                        required
                        fullWidth
                        type="password"
                        label="Yeni şifrenizi giriniz"
                        value={newPasswordVal}
                        onChange={(e) => setNewPasswordVal(e.target.value)}
                    />
                    
                    <TextField
                        required
                        fullWidth
                        type="password"
                        label="Yeni şifrenizi tekrar giriniz"
                        value={confirmPasswordVal}
                        onChange={(e) => setConfirmPasswordVal(e.target.value)}
                    />

                    <LoadingButton
                        fullWidth
                        size="large"
                        type="submit"
                        variant="contained"
                        loading={loading}
                        style={{ backgroundColor: '#dc3545' }}
                    >
                        Şifreyi Güncelle ve Giriş Yap
                    </LoadingButton>
                </Stack>
            </form>
        </Stack>
    );
  }

  return (
    <>
      <form id="login-form" onSubmit={handleLoginSubmit}>
        <Stack spacing={3}>
          <TextField
            required
            id="email"
            name="email"
            label="Kullanıcı adını girin"
            autoComplete="off"
            value={loginEmail}
            onChange={(e) => setLoginEmail(e.target.value)}
          />

          <TextField
            required
            id="password"
            name="password"
            label="Şifrenizi girin"
            value={loginPassword}
            onChange={(e) => setLoginPassword(e.target.value)}
            type={showPassword ? 'text' : 'password'}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowPassword(!showPassword)}
                    edge="end"
                  >
                    <Iconify
                      icon={showPassword ? 'eva:eye-fill' : 'eva:eye-off-fill'}
                    />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Stack>

        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ my: 2 }}
        >
          <div>
            <Checkbox
              checked={isChecked}
              value={isChecked}
              onChange={checkboxChange}
              sx={{
                color: '#DC3545',
                '&.Mui-checked': {
                  color: '#DC3545',
                },
              }}
            />
            <label
              style={{ textDecorationLine: 'underline', color: '#DC3545', cursor: 'pointer' }}
              onClick={() => setModal(true)}
            >
              Aydınlatma Metni
            </label>
          </div>    
          <Typography
            onClick={() => navigate('/forgot-password')}
            sx={{ color: '#DC3545', cursor: 'pointer' }}
          >
            Şifremi Unuttum
          </Typography>    
        </Stack>

        <LoadingButton
          disabled={loading || !isChecked}
          fullWidth
          size="large"
          type="submit"
          variant="contained"
          loading={loading}
          style={{ backgroundColor: '#dc3545' }}
        >
          Giriş Yap
        </LoadingButton>
      </form>

      <Dialog open={modal}>
        <DialogTitle>Aydınlatma Metni</DialogTitle>
        <DialogContent>
          <DialogContentText dangerouslySetInnerHTML={{ __html: html }} />
        </DialogContent>

        <DialogActions>
          <Button
            sx={{ backgroundColor: 'green', color: 'white', '&:hover': { backgroundColor: 'darkgreen' } }}
            onClick={() => {
              setChecked(true);
              setModal(false);
            }}
          >
            Okudum, Anladım, Onaylıyorum
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
